/**
 * Moving a parcel's declared weight somewhere else when the parcel itself goes away.
 *
 * `GetItemWeight` (SmartSendSummaryGenerator.ts:78) SUMS parcel weights whenever a consignment
 * exists, so the sum of the parcels IS the shipment weight the customer is quoted on. Anything that
 * removes a parcel therefore has to rehome its weight, or the shipment silently under-declares -
 * which is an under-quote and a carrier surcharge chargeback.
 *
 * `moveContentsToParcel` prunes every parcel it empties and has never done this, so the existing
 * "Move to Parcel N" in the arranger has been losing weight; see bindParcelEdits.
 */
import { ConsignmentApiModel, ConsignmentParcelApiModel } from '~/lib/api/smartsend/types';
import { clearLocalUnitsEntryFields } from '../helpers/DimensionUnits';
import { splitWeightByUnit } from './allocate';

const sum = (ns: number[]) => ns.reduce((a, b) => a + b, 0);

/** Weights are declared to 2dp, and repeated absorbs would otherwise accumulate float noise. */
const toDeclaredWeight = (kg: number): number => Number(kg.toFixed(2));

/**
 * Reweighs every parcel by the number of units it holds, rather than splitting the total evenly.
 *
 * The even split (SplitConsignmentWeight) is right for a CONSIGNMENT - one shipment in several boxes,
 * where the parcel weights only have to sum to the shipment weight. It is wrong wherever each box
 * becomes its own shipment priced on its own weight: a box holding five items and a box holding one
 * would declare the same weight, so an uneven distribution misdeclares both. Convert already avoids
 * this with splitWeightByUnit; this is the plan-level form of the same rule.
 *
 * The per-unit rate comes from the PLAN's own unit total, not the order's, so the box weights always
 * sum back to `totalWeight`. That differs from Convert on purpose: Convert lets the customer ship fewer
 * units than were ordered, so its total legitimately shrinks, whereas a plan here always holds every
 * row and has no way to drop one.
 *
 * Only Weight is touched - length, width and height describe the box, not its contents.
 */
export const applyPerUnitWeight = (plan: ConsignmentApiModel, totalWeight: number): ConsignmentApiModel => {
  if (!plan?.Parcels?.length) {
    return plan;
  }

  const quantities = plan.Parcels.map((p) => (p.Contents ?? []).reduce((units, row) => units + (row.Quantity || 1), 0));
  const weights = splitWeightByUnit(totalWeight, sum(quantities), quantities);

  return {
    Parcels: plan.Parcels.map((p, index) => ({
      ...p,
      Dimensions: { ...p.Dimensions, Weight: weights[index] }
    }))
  };
};

/** Units a parcel declares - a row with no quantity counts as one, matching the allocator. */
const unitsIn = (parcel: ConsignmentParcelApiModel): number =>
  (parcel.Contents ?? []).reduce((units, row) => units + (row.Quantity || 1), 0);

const withWeight = (parcel: ConsignmentParcelApiModel, weight: number): ConsignmentParcelApiModel => {
  // A fresh Dimensions object - a parcel can arrive without one at all.
  const next = { ...parcel, Dimensions: { ...parcel.Dimensions, Weight: toDeclaredWeight(Math.max(0, weight)) } };

  // A computed weight was never typed by anyone, so an as-entered imperial original would now display a
  // number contradicting what is stored and booked.
  if (next.Dimensions.Weight !== parcel.Dimensions?.Weight && parcel.LocalUnits?.originalWeightLb != null) {
    next.LocalUnits = clearLocalUnitsEntryFields(parcel.LocalUnits, ['weight']);
  }

  return next;
};

/**
 * Moves the share of weight belonging to the rows that were moved, out of the boxes they left and into
 * the box they landed in.
 *
 * Without this, weight only followed a box that VANISHED: dragging or moving a row out of a box that
 * still had contents left that box declaring weight it no longer held.
 *
 * The share is proportional to units - three units out of a four-unit, 8kg box carry 6kg - so the
 * shipment total is conserved and a weight the customer typed on a box is respected rather than
 * recomputed away. When the moved rows were a box's last, their units ARE its units, so the share is the
 * whole weight and a pruned box hands all of it over.
 *
 * One rule for both routes into a move: the drag, and the arranger's "Move to Parcel N" checkboxes.
 * They used to disagree, which is how the drag shipped declaring stale weights.
 *
 * Pure. `before` is read for the shares, `after` is what gets rewritten.
 */
export const moveRowsWeight = (
  before: ConsignmentApiModel,
  after: ConsignmentApiModel,
  movedUIDs: string[]
): ConsignmentApiModel => {
  const landing = after?.Parcels?.find((p) => p.Contents?.some((row) => movedUIDs.includes(row.UID)));

  if (!landing || !before?.Parcels?.length) {
    return after;
  }

  // Keyed by parcel UID so a selection spanning several boxes debits each of them.
  const shares = new Map<string, number>();

  before.Parcels.forEach((source) => {
    if (source.UID === landing.UID) {
      return;
    }

    const sourceUnits = unitsIn(source);
    const sourceWeight = source.Dimensions?.Weight ?? 0;
    const movedUnits = (source.Contents ?? [])
      .filter((row) => movedUIDs.includes(row.UID))
      .reduce((units, row) => units + (row.Quantity || 1), 0);

    if (movedUnits > 0 && sourceUnits > 0 && sourceWeight > 0) {
      shares.set(source.UID, toDeclaredWeight(sourceWeight * (movedUnits / sourceUnits)));
    }
  });

  const total = sum([...shares.values()]);

  if (!(total > 0)) {
    return after;
  }

  return {
    Parcels: after.Parcels.map((p) => {
      if (p.UID === landing.UID) {
        return withWeight(p, (p.Dimensions?.Weight ?? 0) + total);
      }
      // A source absent from `after` was pruned by the move, so there is nothing left to debit.
      const share = shares.get(p.UID);
      return share ? withWeight(p, (p.Dimensions?.Weight ?? 0) - share) : p;
    })
  };
};
